<template>
  <v-dialog :value="isActive" @click:outside="closeDialog()" max-width="600" scrollable>
    <ValidationObserver v-slot="{ handleSubmit }">
      <v-card :loading="isLoading">
        <v-card-title class="pa-0 shadow-1">
          <v-img
            :src="require('@/assets/illustrations/bg_modal_card.svg')"
            cover
            position="left bottom"
            class="align-center"
            height="65px"
          >
            <v-card-title class="font-weight-medium primary--text">
              {{ isEditMode ? "Actualizar" : "Crear" }} opción
            </v-card-title>
          </v-img>
        </v-card-title>

        <vuescroll :ops="{ bar: { background: '#bacfcec0', keepShow: true } }">
          <v-card-text style="max-height: 500px">
            <!-- Tabs/Toggle para cambiar de modo al CREAR una nueva opción -->
            <v-row v-if="!isEditMode" class="mb-3">
              <v-col cols="12" class="pb-0">
                <v-btn-toggle v-model="createMode" mandatory color="primary" class="d-flex rounded-lg">
                  <v-btn value="new" class="flex-grow-1" text>Crear opción</v-btn>
                  <v-btn value="shared" class="flex-grow-1" text>Usar opción existente</v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>

            <!-- BOT AL QUE SE VINCULARÁ LA OPCIÓN (CREACIÓN) -->
            <v-alert
              v-if="!isEditMode && (parentName || resolvedBotId)"
              type="info"
              text
              class="mb-4"
            >
              La opción se vinculará al bot:
              <strong>{{ parentName || "Bot #" + resolvedBotId }}</strong>
            </v-alert>

            <!-- AVISO AL EDITAR OPCIÓN COMPARTIDA -->
            <v-alert v-if="isEditMode && isSharedOption" type="info" text class="mb-4">
              El texto y el comportamiento se editan en la opción original
              <strong v-if="sharedOwnerBotName"> (Bot: {{ sharedOwnerBotName }})</strong>.
            </v-alert>

            <!-- AVISO AL EDITAR OPCIÓN ORIGINAL REUTILIZADA EN OTROS BOTS -->
            <v-alert
              v-if="isEditMode && !isSharedOption && dataToEdit.shared_by_count > 0"
              type="warning"
              text
              class="mb-4"
            >
              Los cambios se reflejarán en los <strong>{{ dataToEdit.shared_by_count }}</strong> bots que la usan.
            </v-alert>

            <v-row>
              <!-- NÚMERO LOCAL DE LA OPCIÓN (Siempre editable) -->
              <v-col cols="12" class="pb-0">
                <ValidationProvider name="opción" rules="required|integer" v-slot="{ errors }">
                  <v-select
                    v-model="payload.option"
                    :items="optionNumberItems"
                    :loading="isLoadingBotOptions"
                    :error-messages="errors"
                    label="Número de Opción en el menú (0-9)"
                    filled
                    rounded
                    class="rounded-lg"
                  >
                    <template v-slot:selection="{ item }">{{ item.value }}</template>
                  </v-select>
                </ValidationProvider>

                <!-- Mapa de números usados/libres del bot -->
                <div class="text-center">
                  <v-chip
                    v-for="n in allOptionNumbers"
                    :key="n"
                    x-small
                    class="ma-1"
                    :color="usedOptionNumbers.includes(n) ? 'blue-grey' : 'success'"
                    :outlined="usedOptionNumbers.includes(n)"
                  >
                    {{ n }}
                  </v-chip>
                </div>
                <div class="caption text-center grey--text">
                  Disponibles en verde · Usados en gris
                </div>
                <div v-if="freeOptionNumbers.length === 0" class="caption error--text text-center mt-1">
                  Este bot ya usa todos los números (0-9).
                </div>
              </v-col>

              <!-- MODO: USAR OPCIÓN EXISTENTE (CREACIÓN) -->
              <template v-if="!isEditMode && createMode === 'shared'">
                <v-col cols="12" class="pb-0">
                  <ValidationProvider name="opción compartida" rules="required" v-slot="{ errors }">
                    <v-autocomplete
                      v-model="selectedSharedOptionId"
                      :items="filteredAvailableOptions"
                      :loading="isLoadingAvailable"
                      item-text="message"
                      item-value="id"
                      label="Seleccionar opción reutilizable"
                      filled
                      rounded
                      class="rounded-lg"
                      :error-messages="errors"
                      @change="onSelectSharedOption"
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-bold">
                            [{{ item.option }}] {{ item.message }} — Bot: {{ item.owner_bot ? item.owner_bot.name : '' }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            Acción: {{ getActionHint(item) }}
                            <span v-if="item.shared_by_count > 0"> • Usada en {{ item.shared_by_count }} bots</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                      <template v-slot:selection="{ item }">
                        [{{ item.option }}] {{ item.message }} — Bot: {{ item.owner_bot ? item.owner_bot.name : '' }}
                      </template>
                    </v-autocomplete>
                  </ValidationProvider>
                </v-col>

                <!-- PREVIEW DE LA OPCIÓN SELECCIONADA -->
                <v-col cols="12" v-if="selectedSharedOption">
                  <v-card outlined class="pa-3 bg-light rounded-lg">
                    <div class="subtitle-2 primary--text mb-1">Vista previa (Solo lectura):</div>
                    <div><strong>Mensaje:</strong> {{ selectedSharedOption.message }}</div>
                    <div><strong>Comportamiento:</strong> {{ getActionHint(selectedSharedOption) }}</div>
                    <div v-if="selectedSharedOption.owner_bot">
                      <strong>Bot de origen:</strong> {{ selectedSharedOption.owner_bot.name }}
                    </div>
                  </v-card>
                </v-col>
              </template>

              <!-- CAMPOS NORMALES (Solo visibles si NO es compartida) -->
              <template v-if="shouldShowFullForm">
                <!-- Mensaje -->
                <v-col cols="12" class="pb-0">
                  <ValidationProvider name="mensaje menú" rules="required|max:25" v-slot="{ errors }">
                    <v-textarea
                      v-model="payload.message"
                      :error-messages="errors"
                      label="Mensaje menú"
                      rounded
                      filled
                      class="rounded-lg"
                      counter="25"
                      maxlength="25"
                    ></v-textarea>
                  </ValidationProvider>
                </v-col>

                <!-- Mensaje de regreso -->
                <v-col cols="12" class="pb-0">
                  <ValidationProvider
                    name="mensaje de regreso al menú anterior"
                    rules="max:70"
                    v-slot="{ errors }"
                  >
                    <v-textarea
                      v-model="payload.option_return"
                      :error-messages="errors"
                      label="Mensaje de regreso al menú anterior"
                      rounded
                      filled
                      class="rounded-lg"
                      counter="70"
                      maxlength="70"
                    ></v-textarea>
                  </ValidationProvider>
                </v-col>

                <!-- ACCIÓN DE LA OPCIÓN (solo una) -->
                <v-col cols="12" class="py-0">
                  <div class="subtitle-2 primary--text mb-2">¿Qué hace esta opción al ser elegida?</div>
                  <v-btn-toggle v-model="actionType" mandatory color="primary" class="d-flex flex-wrap rounded-lg">
                    <v-btn value="queue" class="flex-grow-1">
                      <v-icon small left>mdi-phone-in-talk</v-icon>
                      <span>Redirigir a cola</span>
                    </v-btn>
                    <v-btn value="notification" class="flex-grow-1">
                      <v-icon small left>mdi-bell-outline</v-icon>
                      <span>Notificación</span>
                    </v-btn>
                    <v-btn value="survey" class="flex-grow-1">
                      <v-icon small left>mdi-clipboard-list-outline</v-icon>
                      <span>Encuesta</span>
                    </v-btn>
                  </v-btn-toggle>
                </v-col>

                <!-- Cola destino -->
                <v-col v-if="actionType === 'queue'" cols="12">
                  <ValidationProvider name="Cola" rules="required" v-slot="{ errors }">
                    <v-autocomplete
                      :disabled="isLoading"
                      :items="queueList"
                      item-text="name"
                      item-value="id"
                      required
                      filled
                      rounded
                      class="rounded-small"
                      dense
                      label="Cola"
                      name="number"
                      prepend-inner-icon="mdi-numeric"
                      color="blue-grey"
                      v-model="payload.queue_id"
                      :error-messages="errors"
                    >
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item two-line selectable v-on="on" v-bind="attrs" class="pl-3">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.number }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </ValidationProvider>
                </v-col>

                <!-- Texto de notificación -->
                <v-col cols="12" class="pb-0" v-if="actionType === 'notification'">
                  <ValidationProvider name="texto de notificación" rules="required" v-slot="{ errors }">
                    <v-textarea
                      v-model="payload.notification_text"
                      :error-messages="errors"
                      label="Texto de notificación"
                      rounded
                      filled
                      class="rounded-lg"
                    ></v-textarea>
                  </ValidationProvider>
                </v-col>

                <!-- Encuesta (collect data) -->
                <v-col cols="12" class="py-0" v-if="actionType === 'survey'">
                  <ValidationProvider name="encuesta" rules="required" v-slot="{ errors }">
                    <v-select
                      :disabled="isLoading"
                      v-model="payload.collect_data_id"
                      :items="collectDataList"
                      :error-messages="errors"
                      item-text="title"
                      item-value="id"
                      label="Encuesta (collect data)"
                      filled
                      rounded
                      class="rounded-lg"
                    >
                      <template v-slot:item="{ item, on, attrs }">
                        <v-list-item two-line selectable v-on="on" v-bind="attrs" class="pl-3">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.title }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-select>
                  </ValidationProvider>
                </v-col>
              </template>
            </v-row>
          </v-card-text>
        </vuescroll>

        <v-card-actions>
          <v-btn @click="closeDialog()" class="px-8" color="error" rounded large text>
            Cerrar
          </v-btn>
          <v-spacer></v-spacer>

          <v-btn
            @click="handleSubmit(submitForm)"
            :disabled="isLoading || freeOptionNumbers.length === 0"
            class="px-8"
            color="success"
            rounded
            large
          >
            {{ isEditMode ? "Actualizar" : "Crear" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </ValidationObserver>
  </v-dialog>
</template>

<script>
import vuescroll from "vuescroll";
import { PbxHttpClient } from "@/services/httpclient";
import { extractApiError } from "@/utils";
import axios from "axios";

export default {
  components: {
    vuescroll,
  },

  props: {
    isActive: {
      type: Boolean,
    },
    parentId: {
      type: Number,
      default: null,
    },
    parentName: {
      type: String,
      default: "",
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    dataToEdit: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isLoading: false,
      isLoadingAvailable: false,
      isLoadingBotOptions: false,
      createMode: "new", // 'new' | 'shared'
      actionType: "queue", // 'queue' | 'notification' | 'survey'
      usedOptionNumbers: [],
      selectedSharedOptionId: null,
      selectedSharedOption: null,
      availableOptions: [],
      queueList: [],
      collectDataList: [],
      payload: {
        option: null,
        message: "",
        option_return: "",
        notification_text: "",
        redirection: 0,
        notification: 0,
        queue_id: null,
        bot_id: null,
        collect_data_id: null,
      },
    };
  },

  computed: {
    isEditMode() {
      return !!(this.dataToEdit && this.dataToEdit.id);
    },
    isSharedOption() {
      return !!(
        this.dataToEdit &&
        (this.dataToEdit.shared_option_id != null || this.dataToEdit.shared_option)
      );
    },
    sharedOwnerBotName() {
      if (this.dataToEdit && this.dataToEdit.shared_option && this.dataToEdit.shared_option.owner_bot) {
        return this.dataToEdit.shared_option.owner_bot.name;
      }
      return "";
    },
    shouldShowFullForm() {
      if (this.isEditMode) {
        return !this.isSharedOption;
      }
      return this.createMode === "new";
    },
    // El bot al que queda vinculada la opción. En creación puede llegar por
    // parentId (árbol de opciones) o por data.bot_id (listado de bots);
    // en edición manda el bot_id real de la opción que se está editando.
    resolvedBotId() {
      if (this.isEditMode) {
        return this.dataToEdit.bot_id ?? this.parentId ?? null;
      }
      return this.parentId ?? (this.data && this.data.bot_id) ?? null;
    },
    filteredAvailableOptions() {
      // Excluir opciones que pertenezcan al mismo bot actual
      return this.availableOptions.filter(
        (opt) => opt.bot_id !== this.resolvedBotId && (opt.owner_bot ? opt.owner_bot.id !== this.resolvedBotId : true)
      );
    },
    allOptionNumbers() {
      return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
    },
    freeOptionNumbers() {
      return this.allOptionNumbers.filter((n) => !this.usedOptionNumbers.includes(n));
    },
    optionNumberItems() {
      // Números del menú con su estado: los ya usados por el bot quedan bloqueados
      return this.allOptionNumbers.map((n) => ({
        text: this.usedOptionNumbers.includes(n) ? `${n} (usada)` : String(n),
        value: n,
        disabled: this.usedOptionNumbers.includes(n),
      }));
    },
  },

  created() {
    this.getSelectsInfo();
    this.payload = { ...this.dataToEdit, bot_id: this.resolvedBotId };

    // Preseleccionar la acción actual de la opción (en edición)
    this.actionType =
      this.dataToEdit.collect_data_id != null
        ? "survey"
        : Number(this.dataToEdit.notification) === 1
        ? "notification"
        : "queue";

    this.getBotOptionNumbers();

    if (!this.isEditMode) {
      this.getAvailableOptions();
    }
  },

  watch: {
    parentId(val) {
      this.payload.bot_id = val;
    },
    createMode(val) {
      if (val === "shared" && this.availableOptions.length === 0) {
        this.getAvailableOptions();
      }
    },
  },

  methods: {
    closeDialog(data = false) {
      this.$emit("close:dialog", data, this.isEditMode);
    },

    async getSelectsInfo() {
      let endpoints = ["/clients/index_whatsapp", "/collect_data_survey"];

      await axios
        .all(endpoints.map((endpoint) => PbxHttpClient.get(endpoint)))
        .then((res) => {
          this.queueList = res[0].data.data;
          this.collectDataList = res[1].data;
        })
        .catch(() => {
          this.$snotify.error("Hubo un error al cargar las listas.", "Error");
        });
    },

    async getAvailableOptions() {
      this.isLoadingAvailable = true;
      try {
        const { data } = await PbxHttpClient.get("/whatsapps/options_available");
        this.availableOptions = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error al obtener opciones disponibles:", error);
      } finally {
        this.isLoadingAvailable = false;
      }
    },

    onSelectSharedOption(id) {
      this.selectedSharedOption = this.availableOptions.find((opt) => opt.id === id) || null;
    },

    async getBotOptionNumbers() {
      if (!this.resolvedBotId) return;
      this.isLoadingBotOptions = true;
      try {
        const { data } = await PbxHttpClient.get(`/whatsapps/options_bot/${this.resolvedBotId}`);
        const options = Array.isArray(data) ? data : [];
        // En edición, el número actual de la opción sigue estando disponible
        this.usedOptionNumbers = options
          .filter((opt) => !(this.isEditMode && opt.id === this.dataToEdit.id))
          .map((opt) => parseInt(opt.option));
      } catch (error) {
        console.error("Error al obtener las opciones del bot:", error);
      } finally {
        this.isLoadingBotOptions = false;
      }
    },

    getActionHint(item) {
      if (!item) return "";
      if (item.sub_menu_count > 0) return "Submenú";
      if (item.redirection === 1) return `Redirección a cola ID: ${item.queue_id || 'N/A'}`;
      if (item.collect_data_id) return `Encuesta ID: ${item.collect_data_id}`;
      if (item.notification === 1) return "Notificación";
      return "Mensaje informativo";
    },

    async submitForm() {
      this.isLoading = true;

      // El selector de acción define el comportamiento; el backend exige
      // exactamente una opción activa (redirection | notification | collect_data_id)
      const actionPayload = {
        redirection: this.actionType === "queue" ? 1 : 0,
        notification: this.actionType === "notification" ? 1 : 0,
        queue_id: this.actionType === "queue" ? this.payload.queue_id ?? null : null,
        collect_data_id: this.actionType === "survey" ? this.payload.collect_data_id ?? null : null,
        notification_text: this.actionType === "notification" ? this.payload.notification_text || "" : "",
      };

      try {
        let response;

        if (this.isEditMode) {
          // Si es una opción compartida, SOLO se envía { option }
          if (this.isSharedOption) {
            const updatePayload = {
              option: parseInt(this.payload.option),
            };
            response = await PbxHttpClient.put(
              `/whatsapps/options_bot/${this.dataToEdit.id}`,
              updatePayload
            );
          } else {
            // Opción normal
            const { id, ...normalPayload } = { ...this.payload, ...this.data, ...actionPayload };
            normalPayload.bot_id = this.resolvedBotId;
            normalPayload.option_return = normalPayload.option_return || "";
            normalPayload.notification_text = normalPayload.notification_text || "";
            response = await PbxHttpClient.put(`/whatsapps/options_bot/${id}`, normalPayload);
          }
        } else {
          // CREACIÓN
          if (this.createMode === "shared") {
            const sharedPayload = {
              option: parseInt(this.payload.option),
              bot_id: this.resolvedBotId,
              shared_option_id: this.selectedSharedOptionId,
            };
            response = await PbxHttpClient.post("/whatsapps/options_bot", sharedPayload);
          } else {
            // Opción normal
            const newPayload = {
              ...this.payload,
              ...this.data,
              ...actionPayload,
              bot_id: this.resolvedBotId,
              option_return: this.payload.option_return || "",
              notification_text: this.payload.notification_text || "",
            };
            response = await PbxHttpClient.post("/whatsapps/options_bot", newPayload);
          }
        }

        const resOption = response.data.option || response.data;
        this.closeDialog(resOption);
        this.$snotify.success(
          `Opción ${this.isEditMode ? "actualizada" : "creada"} con éxito.`,
          "¡Hecho!"
        );
      } catch (err) {
        console.error("Error al guardar opción:", err);
        this.$snotify.error(extractApiError(err), "¡Error!");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>